import { Alert, Button, Tag } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import './index.scss';

// WS-13 (ENG-332) — persistent QBO Time mismatch banner on the Employee Cost
// screen. After an employee sync returns unmatched QBO DisplayNames, this surfaces
// a banner with a "Review now" CTA. Dismissing it collapses to a small count badge
// that persists until the mismatches are resolved (Functional Spec §10.2). All
// state is owned by the host screen; this component is presentational.
interface QboTimeMismatchBannerProps {
	unmatchedNames: string[]; // current unmatched set from GET /employees/unmatched-names
	onReviewNow: () => void; // opens the resolution panel
	onDismiss: () => void; // collapses banner → badge
	onRestore: () => void; // re-expands badge → banner
	dismissed: boolean; // controlled by host screen local state
}

export default function QboTimeMismatchBanner({
	unmatchedNames,
	onReviewNow,
	onDismiss,
	onRestore,
	dismissed,
}: QboTimeMismatchBannerProps) {
	const count = unmatchedNames.length;

	// Nothing to surface — the host also guards on this, but the component is
	// safe to render unconditionally.
	if (count === 0) return null;

	const plural = count === 1 ? '' : 's';

	// Collapsed state: a compact, persistent chip the user can re-expand.
	if (dismissed) {
		return (
			<div className="qbo-mismatch-badge">
				<Tag color="warning" className="qbo-mismatch-badge__tag">
					<WarningOutlined /> {count} unmatched employee{plural}
				</Tag>
				<Button
					type="link"
					size="small"
					className="qbo-mismatch-badge__review"
					onClick={onRestore}
				>
					Review
				</Button>
			</div>
		);
	}

	// Expanded state: full banner. We do NOT use Alert's `closable` (that removes
	// the element); the dismiss control collapses to the badge instead.
	return (
		<Alert
			className="qbo-mismatch-banner"
			type="warning"
			banner
			showIcon
			closable={false}
			message={`${count} employee name${plural} from QBO sync couldn't be matched`}
			action={
				<div className="qbo-mismatch-banner__actions">
					<Button size="small" type="primary" onClick={onReviewNow}>
						Review now
					</Button>
					<Button
						size="small"
						type="text"
						aria-label="Dismiss"
						onClick={onDismiss}
					>
						✕
					</Button>
				</div>
			}
		/>
	);
}
